import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useModal } from 'vue-final-modal';
import DangerIcon from '@/shared/assets/icons/DangerIcon.svg';
import EraserIcon from '@/shared/assets/icons/EraserIcon.svg';
import ConfirmDeleteModal from '@/shared/ui/modals/ConfirmDeleteModal.vue';

import {
  permissionSubjectClearSuccessMessage,
  permissionSubjectClearTitle,
  PERMISSION_APPLY_ERROR_MESSAGE,
  PERMISSION_CLEAR_DESCRIPTION,
  PERMISSION_APPLY_SUCCESS_MESSAGE,
  PERMISSION_DIFFERENT_APPLY_DESCRIPTION,
  PERMISSION_DIFFERENT_APPLY_TITLE,
} from '../lib/permission-subject-filters';
import { useFormBuilderStore } from '../model/form-builder.store';
import { useFormPermissionsSelectionStore } from '../model/form-permissions-selection.store';
import { useFormPermissionsStore } from '../model/form-permissions.store';

import { useFormBuilderToast } from './useFormBuilderToast';
import type { PermissionSubjectMode } from './usePermissionSubjects';
import type { PermissionFieldTarget, PermissionSubjects } from '../model/form-permissions.store';

const SUBJECT_KEY: Record<PermissionSubjectMode, keyof PermissionSubjects> = {
  positions: 'positionIds',
  functionalActivities: 'functionalActivityIds',
};

const sortedIds = (ids: string[]): string[] => [...ids].sort();

export function usePermissionSubjectActions(mode: PermissionSubjectMode) {
  const key = SUBJECT_KEY[mode];
  const formBuilderStore = useFormBuilderStore();
  const selectionStore = useFormPermissionsSelectionStore();
  const permissionsStore = useFormPermissionsStore();
  const { addMode, confirmedItems, clickedFieldId } = storeToRefs(selectionStore);
  const { showSuccessToast, showErrorToast } = useFormBuilderToast();

  const selectedIds = ref<string[]>([]);
  const appliedIds = ref<string[]>([]);

  const targets = computed<PermissionFieldTarget[]>(() => {
    if (confirmedItems.value.length === 0) return [];
    return selectionStore.resolveConfirmedTargets(formBuilderStore.elements);
  });

  // A field picked by click only mirrors its grants; the apply and clear actions still need a confirmed selection.
  const mirroredTargets = computed<PermissionFieldTarget[]>(() => {
    if (targets.value.length > 0) return targets.value;
    if (clickedFieldId.value === null || addMode.value) return [];

    return selectionStore.resolveItemTargets(
      [{ kind: 'field', id: clickedFieldId.value, displayId: '', label: '' }],
      formBuilderStore.elements
    );
  });

  const isSectionDisabled = computed(() => addMode.value || targets.value.length === 0);
  const isDirty = computed(
    () => sortedIds(selectedIds.value).join() !== sortedIds(appliedIds.value).join()
  );
  const isApplyDisabled = computed(() => isSectionDisabled.value || !isDirty.value);
  const isClearDisabled = computed(
    () => isSectionDisabled.value || selectedIds.value.length === 0
  );

  function existingIdsOf(target: PermissionFieldTarget): number[] {
    return permissionsStore.getFieldPermission(target.fieldId)?.[key] ?? [];
  }

  function syncFromMirroredTargets() {
    const [onlyTarget, ...rest] = mirroredTargets.value;
    const ids = onlyTarget && rest.length === 0 ? existingIdsOf(onlyTarget).map(String) : [];

    selectedIds.value = ids;
    appliedIds.value = [...ids];
  }

  // Tracks the grants as well as the field ids, so a paste onto the same selection resyncs the checkboxes.
  const mirrorSignature = computed(() =>
    mirroredTargets.value
      .map((target) => `${target.fieldId}:${existingIdsOf(target).join(',')}`)
      .join('|')
  );

  watch(mirrorSignature, syncFromMirroredTargets, { immediate: true });

  function openConfirmModal(attrs: {
    icon: string | object;
    iconAlt: string;
    title: string;
    description: string;
    onConfirmed: () => void;
  }) {
    const { open, close } = useModal({
      component: ConfirmDeleteModal,
      attrs: {
        icon: attrs.icon,
        iconAlt: attrs.iconAlt,
        title: attrs.title,
        description: attrs.description,
        onClose() {
          close();
        },
        onConfirm() {
          close();
          attrs.onConfirmed();
        },
      },
    });

    open();
  }

  function commitApply() {
    try {
      selectionStore.recordChange(() =>
        permissionsStore.applySubjectsToFields(targets.value, key, selectedIds.value.map(Number))
      );
      appliedIds.value = [...selectedIds.value];
      showSuccessToast({ title: PERMISSION_APPLY_SUCCESS_MESSAGE, type: 'success' });
    } catch {
      showErrorToast({ title: PERMISSION_APPLY_ERROR_MESSAGE, type: 'error' });
    }
  }

  function apply() {
    if (isApplyDisabled.value) return;

    const replacesExisting =
      targets.value.length > 1 && targets.value.some((target) => existingIdsOf(target).length > 0);

    if (!replacesExisting) {
      commitApply();
      return;
    }

    openConfirmModal({
      icon: DangerIcon,
      iconAlt: 'Alerta',
      title: PERMISSION_DIFFERENT_APPLY_TITLE,
      description: PERMISSION_DIFFERENT_APPLY_DESCRIPTION,
      onConfirmed: commitApply,
    });
  }

  function clear() {
    if (isClearDisabled.value) return;

    openConfirmModal({
      icon: EraserIcon,
      iconAlt: 'Limpar',
      title: permissionSubjectClearTitle(mode),
      description: PERMISSION_CLEAR_DESCRIPTION,
      onConfirmed() {
        selectionStore.recordChange(() =>
          permissionsStore.applySubjectsToFields(targets.value, key, [])
        );
        selectedIds.value = [];
        appliedIds.value = [];
        showSuccessToast({ title: permissionSubjectClearSuccessMessage(mode), type: 'success' });
      },
    });
  }

  return {
    selectedIds,
    isSectionDisabled,
    isApplyDisabled,
    isClearDisabled,
    apply,
    clear,
  };
}
